import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Sparkles, X, SendHorizonal, Loader2, Check, ArrowUpRight } from "lucide-react";

const SUGGESTIONS = [
  "Which bids are owed a reply?",
  "What's due this week?",
  "Summarize my pipeline",
  "Which pursuits are going cold?",
];

function Rich({ text }) {
  return (text || "").split("\n").map((ln, i) => {
    if (ln.trim() === "---") return <div key={i} className="border-t border-slate-200 my-1.5" />;
    const h = ln.match(/^#{1,6}\s+(.*)$/);
    const bullet = ln.match(/^\s*[-*]\s+(.*)$/);
    let content = h ? h[1] : bullet ? "• " + bullet[1] : ln;
    const parts = content.split(/(\*\*[^*]+\*\*)/g).map((p, j) =>
      p.startsWith("**") && p.endsWith("**") ? <strong key={j}>{p.slice(2, -2)}</strong> : p);
    return <div key={i} className={h ? "font-semibold text-black mt-1.5" : bullet ? "pl-1" : ""}>{parts}</div>;
  });
}

export default function AssistantPanel() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const sess = useRef(null);
  const endRef = useRef(null);
  const [focus, setFocus] = useState(null);
  const focusRef = useRef(null);
  const askRef = useRef(null);
  useEffect(() => {
    window.askSol = (bid, prefill) => {
      focusRef.current = bid || null; setFocus(bid || null); setOpen(true);
      if (prefill) setTimeout(() => askRef.current?.(prefill), 60);
    };
    return () => { window.askSol = undefined; };
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "j") { e.preventDefault(); setOpen((o) => !o); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs, loading]);

  const ask = async (q) => {
    const question = (q ?? input).trim();
    if (!question || loading) return;
    const next = [...msgs, { role: "user", content: question }];
    setMsgs(next); setInput(""); setLoading(true);
    try {
      const { data } = await api.post("/assistant/chat", {
        message: question, session_id: sess.current, history: next.slice(0, -1),
        bid_id: focusRef.current?.bid_id,
      });
      sess.current = data.session_id;
      let content = data.answer || "";
      let action = null;
      const idx = content.indexOf("[[ACTION]]");
      if (idx >= 0) {
        try { action = JSON.parse(content.slice(idx + 10).trim()); } catch { action = null; }
        content = content.slice(0, idx).trim();
      }
      setMsgs((m) => [...m, { role: "assistant", content, action, refs: data.refs || [] }]);
    } catch (e) {
      setMsgs((m) => [...m, { role: "assistant", content: "I couldn't answer that right now — please try again." }]);
    } finally { setLoading(false); }
  };

  const goRef = (href) => { setOpen(false); navigate(href); };
  askRef.current = ask;

  const ACTION_LABEL = {
    create_lead: "Create this lead",
    snooze_bid: "Snooze this follow-up",
    log_contact: "Log this contact",
    draft_email: "Draft this email",
  };

  const runAction = async (i, action) => {
    setMsgs((m) => m.map((x, j) => (j === i ? { ...x, acting: true } : x)));
    try {
      const { data } = await api.post("/assistant/act", { action });
      setMsgs((m) => m.map((x, j) => (j === i ? { ...x, action: null, acting: false, result: data.message, resultHref: data.href, draft: data.draft, sendTo: data.to, canSend: data.can_send, subject: data.subject, bidId: data.bid_id } : x)));
      if (!data.draft) toast.success(data.message);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Action failed");
      setMsgs((m) => m.map((x, j) => (j === i ? { ...x, acting: false } : x)));
    }
  };

  return (
    <>
      {!open && (
        <button data-testid="assistant-fab" onClick={() => setOpen(true)}
          className="fixed bottom-6 right-6 z-50 flex items-center gap-2 h-12 pl-3 pr-4 rounded-full bg-[#003E87] text-white shadow-xl hover:bg-[#00336f] transition group">
          <span className="grid place-items-center w-7 h-7 rounded-full bg-[#F4C400] text-black"><Sparkles className="w-4 h-4" /></span>
          <span className="text-sm font-semibold">Ask Sol</span>
        </button>
      )}
      {open && (
        <div className="fixed inset-0 z-50 flex justify-end" data-testid="assistant-panel">
          <div className="absolute inset-0 bg-slate-900/30" onClick={() => setOpen(false)} />
          <aside className="relative w-full max-w-md bg-white h-full flex flex-col shadow-2xl">
            <header className="flex items-center justify-between px-5 py-4 border-b border-slate-200 bg-[#000000]">
              <div className="flex items-center gap-2 text-white">
                <span className="grid place-items-center w-7 h-7 rounded-full bg-[#F4C400] text-black"><Sparkles className="w-4 h-4" /></span>
                <div>
                  <div className="font-display font-bold leading-none">Sol</div>
                  <div className="text-[11px] text-slate-400 mt-0.5">{focus ? <>Focused on <b className="text-[#F4C400]">{focus.name}</b> · <button onClick={() => { focusRef.current = null; setFocus(null); }} className="underline hover:text-white">clear</button></> : "Your solar estimating copilot — bids, clients & pipeline"}</div>
                </div>
              </div>
              <button data-testid="assistant-close" onClick={() => setOpen(false)} className="text-slate-400 hover:text-white"><X className="w-5 h-5" /></button>
            </header>

            <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
              {msgs.length === 0 && (
                <div className="text-sm text-slate-500">
                  <p className="mb-3">I can see your whole workspace. Try:</p>
                  <div className="flex flex-wrap gap-2">
                    {SUGGESTIONS.map((s) => (
                      <button key={s} data-testid={`assistant-suggestion`} onClick={() => ask(s)}
                        className="text-xs px-2.5 py-1.5 rounded-full border border-slate-200 text-slate-600 hover:border-[#003E87] hover:text-[#003E87] transition">{s}</button>
                    ))}
                  </div>
                </div>
              )}
              {msgs.map((m, i) => (
                <div key={i} data-testid={`assistant-msg-${m.role}`} className={m.role === "user" ? "flex justify-end" : "flex flex-col items-start gap-2"}>
                  <div className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 text-sm ${m.role === "user" ? "bg-[#003E87] text-white whitespace-pre-wrap self-end" : "bg-slate-100 text-slate-800 space-y-0.5"}`}>{m.role === "assistant" ? <Rich text={m.content} /> : m.content}</div>
                  {m.role === "assistant" && m.refs && m.refs.length > 0 && (
                    <div className="flex flex-wrap gap-1.5" data-testid="assistant-refs">
                      {m.refs.map((r, k) => (
                        <button key={k} data-testid="assistant-ref" onClick={() => goRef(r.href)}
                          className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full border border-slate-200 text-[#003E87] hover:bg-[#003E87] hover:text-white transition">
                          {r.label} <ArrowUpRight className="w-3 h-3" />
                        </button>
                      ))}
                    </div>
                  )}
                  {m.role === "assistant" && m.action && (
                    <div className="w-full max-w-[85%] rounded-xl border border-[#F4C400]/60 bg-[#F4C400]/10 p-3" data-testid="assistant-action-card">
                      <div className="text-xs font-semibold text-[#5c4a00] mb-2">{ACTION_LABEL[m.action.type] || "Run this action"}?</div>
                      <div className="flex gap-2">
                        <Button size="sm" data-testid="assistant-action-confirm" disabled={m.acting} onClick={() => runAction(i, m.action)} className="bg-[#003E87] hover:bg-[#00336f] text-white h-8">
                          {m.acting ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Check className="w-3.5 h-3.5 mr-1" />} Confirm
                        </Button>
                        <Button size="sm" variant="outline" data-testid="assistant-action-dismiss" disabled={m.acting}
                          onClick={() => setMsgs((mm) => mm.map((x, j) => (j === i ? { ...x, action: null } : x)))} className="h-8">Dismiss</Button>
                      </div>
                    </div>
                  )}
                  {m.role === "assistant" && m.result && (
                    <div className="w-full max-w-[85%] text-xs text-emerald-700 flex items-center gap-2" data-testid="assistant-action-result">
                      <Check className="w-3.5 h-3.5" /> {m.result}
                      {m.resultHref && <button onClick={() => goRef(m.resultHref)} className="text-[#003E87] hover:underline inline-flex items-center gap-0.5">Open <ArrowUpRight className="w-3 h-3" /></button>}
                    </div>
                  )}
                  {m.role === "assistant" && m.draft != null && (
                    <div className="w-full max-w-[85%] rounded-lg bg-white border border-slate-200 p-2.5" data-testid="assistant-draft">
                      <textarea data-testid="assistant-draft-text" value={m.draft}
                        onChange={(e) => setMsgs((mm) => mm.map((x, j) => (j === i ? { ...x, draft: e.target.value } : x)))}
                        rows={Math.min(12, Math.max(4, (m.draft.match(/\n/g) || []).length + 3))}
                        className="w-full resize-y text-sm text-slate-700 leading-relaxed bg-transparent outline-none" />
                      <div className="flex items-center gap-3 mt-2 pt-2 border-t border-slate-100">
                        <button onClick={() => { navigator.clipboard.writeText(m.draft); toast.success("Copied"); }} className="text-xs text-[#003E87] hover:underline">Copy</button>
                        {m.sendTo && m.canSend && (
                          <button data-testid="assistant-draft-send"
                            onClick={async () => { if (!m.draft.trim()) { toast.error("Write something first"); return; } try { await api.post("/email/compose-send", { to: m.sendTo, subject: m.subject, text: m.draft, bid_id: m.bidId }); toast.success(`Sent to ${m.sendTo}`); } catch (e) { toast.error(e.response?.data?.detail || "Send failed"); } }}
                            className="text-xs text-emerald-700 hover:underline">Send via Gmail to {m.sendTo} →</button>
                        )}
                        {m.sendTo && !m.canSend && <span className="text-[11px] text-slate-400">Connect Gmail with send access to send in one click.</span>}
                      </div>
                      <div className="text-[11px] text-slate-400 mt-1">Edit freely before sending.</div>
                    </div>
                  )}
                </div>
              ))}
              {loading && <div className="flex items-center gap-2 text-sm text-slate-400"><Loader2 className="w-4 h-4 animate-spin" /> Thinking…</div>}
              <div ref={endRef} />
            </div>

            <div className="border-t border-slate-200 p-3">
              <div className="flex items-end gap-2">
                <textarea data-testid="assistant-input" rows={1} value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); ask(); } }}
                  placeholder="Ask Sol anything…"
                  className="flex-1 resize-none max-h-32 text-sm rounded-lg border border-slate-200 px-3 py-2 outline-none focus:border-[#003E87]" />
                <Button data-testid="assistant-send" onClick={() => ask()} disabled={loading || !input.trim()}
                  className="bg-[#F4C400] hover:bg-[#D9AE00] text-black h-9 w-9 p-0 shrink-0"><SendHorizonal className="w-4 h-4" /></Button>
              </div>
            </div>
          </aside>
        </div>
      )}
    </>
  );
}
